import React, { useRef, useEffect } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);

  // 初始化内容
  useEffect(() => {
    if (editorRef.current && isInitialMount.current) {
      editorRef.current.innerHTML = value || '';
      isInitialMount.current = false;
    }
  }, []);

  // 当外部值改变时更新编辑器（但不在用户输入时）
  useEffect(() => {
    if (editorRef.current && !isInitialMount.current) {
      const currentContent = editorRef.current.innerHTML;
      if (currentContent !== value && document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      editorRef.current.focus();
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* 工具栏 */}
      <div className="border-b border-gray-200 bg-gray-50 p-2 flex flex-wrap gap-1">
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h2>')}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
          title="标题"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h3>')}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
          title="子标题"
        >
          H3
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="px-3 py-1 text-sm font-bold border border-gray-300 rounded hover:bg-gray-100"
          title="粗体"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="px-3 py-1 text-sm italic border border-gray-300 rounded hover:bg-gray-100"
          title="斜体"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="px-3 py-1 text-sm underline border border-gray-300 rounded hover:bg-gray-100"
          title="下划线"
        >
          U
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
          title="无序列表"
        >
          • 列表
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
          title="有序列表"
        >
          1. 列表
        </button>
        <div className="w-px bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => {
            const color = prompt('输入颜色（如：#ff0000 或 red）：');
            if (color) execCommand('foreColor', color);
          }}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
          title="文字颜色"
        >
          🎨 颜色
        </button>
        <button
          type="button"
          onClick={() => execCommand('removeFormat')}
          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100"
          title="清除格式"
        >
          ✕ 清除
        </button>
      </div>

      {/* 编辑区域 */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className="p-4 min-h-[200px] max-h-[400px] overflow-y-auto focus:outline-none prose prose-sm max-w-none"
        data-placeholder={placeholder}
        style={{
          caretColor: 'black',
        }}
      />

      <style>
        {`
          [contenteditable]:empty:before {
            content: attr(data-placeholder);
            color: #9ca3af;
            pointer-events: none;
            display: block;
          }

          [contenteditable] h2 {
            font-size: 1.5em;
            font-weight: bold;
            margin: 0.5em 0;
          }

          [contenteditable] h3 {
            font-size: 1.25em;
            font-weight: bold;
            margin: 0.5em 0;
          }

          [contenteditable] p {
            margin: 0.5em 0;
          }

          [contenteditable] ul,
          [contenteditable] ol {
            margin: 0.5em 0;
            padding-left: 2em;
          }

          [contenteditable] strong {
            font-weight: bold;
          }

          [contenteditable] em {
            font-style: italic;
          }

          [contenteditable] u {
            text-decoration: underline;
          }
        `}
      </style>
    </div>
  );
};

export default RichTextEditor;
